import { useState, useEffect, useCallback, useId } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getOrganizations, createOrganization } from '../../services/admin';
import type { OrganizationSummary, GetOrganizationsParams } from '../../types/admin';
import { TablePagination } from '../../components/TablePagination';
import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { CreateOrganizationNotice, type CreateNotice } from './CreateOrganizationNotice';
import { OrganizationStatusBadge } from './OrganizationStatusBadge';
import { trialCellText, trialStatusOf } from './organizationTrial';
import { AdminPage, AdminPageHeader, AdminPanel } from './AdminPage';
import {
  buildListParams,
  resolveInitialListState,
  saveAdminListState,
} from './adminNavigation';
import { InviteUserDialog } from './invite/InviteUserDialog';
import { PendingSetupInvitations } from './invite/PendingSetupInvitations';
import { useSetupInvitations } from './invite/useSetupInvitations';
import './AdminSection.css';

/**
 * AdminSection displays the list of all organizations.
 * Access control is handled at the route level by ProtectedRoute.
 *
 * Page and search live in the URL, mirrored into sessionStorage. `/admin/organizations/:id` is a
 * sibling route, not a child, so opening an organization unmounts this component — local state put
 * an admin back on page 1 every time they looked at something on page 4.
 *
 * Both layers earn their place: the URL makes a paged list linkable and lets browser back step
 * through pages, while the session cache restores the page after a trip through a section that
 * carries no params of its own. The URL wins when it names anything, so a shared link always shows
 * what it says.
 */
export const AdminSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  /*
    Resolved once, during the first render. Reading it in an effect instead would let the list fetch
    page 1 before correcting itself, which is a visible flash of the wrong page.
  */
  const [initialState] = useState(() => resolveInitialListState(searchParams));
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  const [pageNumber, setPageNumber] = useState(initialState.pageNumber);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Create organization form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSport, setNewOrgSport] = useState('');
  // Blank means the platform default. '0' onboards without messaging.
  const [newOrgQuota, setNewOrgQuota] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newOrgSeedSampleData, setNewOrgSeedSampleData] = useState(true);
  const seedHelpId = useId();
  // Outlives the form, which closes on success: a failed seed has to stay visible after that.
  const [createNotice, setCreateNotice] = useState<CreateNotice | null>(null);
  /*
    The unified invite. Both the button and the pending panel hide when the API answers the setup
    invitation list with 404, which is what an older API does during a web-first deploy.
  */
  const setupInvitations = useSetupInvitations();
  const [inviteOpen, setInviteOpen] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: GetOrganizationsParams = {
        pageNumber,
        pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await getOrganizations(params);
      setOrganizations(response.items);
      setTotalCount(response.totalCount);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      setError('Failed to load organizations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  /*
    Mirror page and search into the URL. `replace` so paging does not bury the previous screen
    under a history entry per page — browser back still steps back through pages, because each
    replace lands on the entry this component owns.
  */
  useEffect(() => {
    const params = buildListParams(pageNumber, searchTerm);
    setSearchParams(params, { replace: true });
    // Cached too, so returning from another section — where the URL carries nothing — still lands
    // on the page the admin was last looking at.
    saveAdminListState(params);
  }, [pageNumber, searchTerm, setSearchParams]);

  /*
    Where "Back to Organizations" should land. Passed as route state rather than relying on browser
    back, because the detail view's own button is an explicit navigate — see OrganizationDetail.
  */
  const backTo = (() => {
    const params = buildListParams(pageNumber, searchTerm).toString();
    return params ? `/admin?${params}` : '/admin';
  })();

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPageNumber(1); // Reset to first page on search
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newOrgName.trim() || !newOrgSport) return;

    setCreateLoading(true);
    setCreateError(null);
    setCreateNotice(null);

    try {
      const created = await createOrganization({
        name: newOrgName.trim(),
        sport: newOrgSport,
        ...(newOrgQuota.trim() === ''
          ? {}
          : { messagingNumberQuota: Number(newOrgQuota) }),
        // Sent explicitly either way, so an unchecked box never falls back to the API's default.
        seedSampleData: newOrgSeedSampleData,
      });

      if (created.sampleData === 'failed') {
        setCreateNotice({ kind: 'failed', organizationId: created.id });
      } else if (created.sampleData === 'skipped') {
        setCreateNotice({ kind: 'skipped' });
      }

      // Reset form and close
      setNewOrgName('');
      setNewOrgSport('');
      setNewOrgQuota('');
      setNewOrgSeedSampleData(true);
      setShowCreateForm(false);

      // Refresh list
      fetchOrganizations();
    } catch (err: unknown) {
      console.error('Failed to create organization:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create organization.';
      setCreateError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <AdminPage className="admin-section">
      <AdminPageHeader
        title="Organizations"
        actions={
          <>
            <div className="admin-section__search">
              <input
                type="text"
                className="admin-section__search-input"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <button
              className="admin-section__add-btn"
              onClick={() => {
                setShowCreateForm(!showCreateForm);
                setCreateNotice(null);
              }}
            >
              {showCreateForm ? 'Cancel' : 'Add Organization'}
            </button>
            {setupInvitations.supported === true && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setInviteOpen(true)}
                data-testid="admin-invite-user"
              >
                Invite user
              </Button>
            )}
          </>
        }
      />

      {showCreateForm && (
        <AdminPanel>
          <form className="admin-section__create-form" onSubmit={handleCreateOrganization}>
            <div className="admin-section__form-row">
              <input
                type="text"
                className="admin-section__input"
                placeholder="Organization Name *"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                required
              />
              <select
                className="admin-section__select"
                value={newOrgSport}
                onChange={(e) => setNewOrgSport(e.target.value)}
                required
              >
                <option value="">Select Sport *</option>
                <option value="baseball">Baseball</option>
                <option value="softball">Softball</option>
              </select>
              <select
                className="admin-section__create-input"
                value={newOrgQuota}
                onChange={(e) => setNewOrgQuota(e.target.value)}
                aria-label="Messaging numbers"
              >
                <option value="">Messaging: default</option>
                <option value="0">No messaging</option>
                <option value="1">1 number</option>
                <option value="2">2 numbers</option>
                <option value="3">3 numbers</option>
                <option value="5">5 numbers</option>
                <option value="10">10 numbers</option>
              </select>
              <button
                type="submit"
                className="admin-section__create-btn"
                disabled={createLoading || !newOrgName.trim() || !newOrgSport}
              >
                {createLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
            <div className="admin-section__seed-field">
              <Checkbox
                label="Seed sample data"
                checked={newOrgSeedSampleData}
                onChange={(e) => setNewOrgSeedSampleData(e.target.checked)}
                aria-describedby={seedHelpId}
                data-testid="create-org-seed-sample-data"
              />
              <p className="admin-section__helper" id={seedHelpId}>
                Adds the sample teams, players, calls and events from Admin &gt; Sample data.
              </p>
            </div>
            {createError && (
              <div className="admin-section__form-error">{createError}</div>
            )}
          </form>
        </AdminPanel>
      )}

      {/* Always mounted: a live region inserted along with its text is not reliably announced. */}
      <div role="status" className="admin-section__status" data-testid="create-org-status">
        {createNotice && <CreateOrganizationNotice notice={createNotice} backTo={backTo} />}
      </div>

      {setupInvitations.supported === true && (
        <PendingSetupInvitations
          list={setupInvitations.list}
          error={setupInvitations.error}
          reload={setupInvitations.reload}
        />
      )}

      {inviteOpen && (
        <InviteUserDialog
          onClose={() => setInviteOpen(false)}
          onSent={() => void setupInvitations.reload()}
        />
      )}

      {error && (
        <div className="admin-error" role="alert">
          {error}
          <button onClick={fetchOrganizations} className="admin-section__retry-btn">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <p className="admin-state">Loading organizations...</p>
      ) : (
        <AdminPanel>
          <div className="admin-section__table-container">
            <table className="admin-section__table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Sport</th>
                  <th>Status</th>
                  <th>Trial</th>
                  <th>Users</th>
                  <th>Prospects</th>
                  <th>Created</th>
                  <th>Last Activity</th>
                  <th>Billing</th>
                </tr>
              </thead>
              <tbody>
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="admin-section__empty">
                      {searchTerm ? 'No organizations match your search' : 'No organizations found'}
                    </td>
                  </tr>
                ) : (
                  organizations.map((org) => (
                    <tr key={org.id} className="admin-section__row">
                      <td className="admin-section__cell-name">
                        <Link to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }} className="admin-section__row-link">
                          {org.name}
                        </Link>
                      </td>
                      <td className="admin-section__cell-sport">
                        <Link to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }} className="admin-section__row-link">
                          {org.sport ? (
                            <span className={`admin-section__sport-badge admin-section__sport-badge--${org.sport}`}>
                              {org.sport}
                            </span>
                          ) : (
                            <span className="admin-section__no-sport">-</span>
                          )}
                        </Link>
                      </td>
                      <td>
                        {/*
                          Every cell wraps its content in the same row link so a
                          click anywhere on the row navigates. Here the link's
                          whole accessible name would be "Active"/"Paused", so
                          it is stripped of link semantics and taken out of the
                          tab order - role="presentation" leaves the badge text
                          itself readable, unlike aria-hidden.
                        */}
                        <Link
                          to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }}
                          className="admin-section__row-link"
                          role="presentation"
                          tabIndex={-1}
                        >
                          <OrganizationStatusBadge
                            isPaused={org.isPaused}
                            trialStatus={trialStatusOf(org)}
                          />
                        </Link>
                      </td>
                      <td className="admin-section__cell-trial">
                        {/*
                          The dates beside the badge, so "who is on trial and who has lapsed" is
                          answerable by scanning rather than by opening each organization. Reads as
                          a dash when the API sent no trial data at all.

                          Stripped of link semantics for the same reason as the Status cell above:
                          this is a summary of the row, not a destination of its own, and most
                          organizations have no trial - so left as a link it would add a tab stop
                          per row whose entire accessible name is "-".
                        */}
                        <Link
                          to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }}
                          className="admin-section__row-link"
                          role="presentation"
                          tabIndex={-1}
                        >
                          {trialCellText(org)}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }} className="admin-section__row-link">
                          {org.userCount}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }} className="admin-section__row-link">
                          {org.prospectCount}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }} className="admin-section__row-link">
                          {formatDate(org.createdAt)}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/admin/organizations/${org.id}`}
                          state={{ from: backTo }} className="admin-section__row-link">
                          {formatDate(org.lastActivityAt)}
                        </Link>
                      </td>
                      {/*
                        The one cell that does not go to the organization page. Billing lives under
                        its own section, so setting an organization up meant finding it here, going
                        to /admin/billing, and finding it a second time in a list that hides
                        organizations with no plan by default.
                      */}
                      <td>
                        <Link
                          to={`/admin/billing/organizations/${org.id}`}
                          className="admin-section__row-link"
                          data-testid={`admin-org-billing-${org.id}`}
                        >
                          Billing
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={pageNumber}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPageNumber}
          />
        </AdminPanel>
      )}
    </AdminPage>
  );
};
